import { findPendingOperation, findPendingOperationWithId } from './utils';
import { types as loginTypes } from '../login';
import { types as homeTypes } from '../home';
import { types as tableTypes } from '../table';
import { types as billTypes } from '../bills';
import { types as driversTypes } from '../drivers';
import { types as bookingTypes } from '../bookings';
import { types as reservationTypes } from '../reservations';
import { types as pricingTypes } from '../pricing';
import { types as companyTypes } from '../companies';
import { types as viaDialogTypes } from '../viaDialog';
import { types as snappayTypes } from '../snappay';
import { types as ticketingTypes } from '../ticketing';
import { types as towerTypes } from '../towers';
import { types as rankTypes } from '../rank';
import { types as driverCommunicationTypes } from '../driver-communication';
import { types as partnerTypes } from '../partners';
import { types as genericTypes } from '../generic';
import { PendingType } from './types';

export const name: string = 'pendingOperations';

export const types = {
    START_PENDING_OPERATION: name + '/START_PENDING_OPERATION',
    STOP_PENDING_OPERATION: name + '/STOP_PENDING_OPERATION',
    CLEAR_ALL: name + '/CLEAR_ALL',
};

export interface PendingOperationStateState {
    actions: PendingType[];
}

const initialState: PendingOperationStateState = {
    actions: [],
};

function isActionAlreadyExist(state: PendingOperationStateState, type: string): boolean {
    return !!state.actions.find((el) => el.type === type);
}

const reducer = (state = initialState, action) => {
    switch (action.type) {
        case types.START_PENDING_OPERATION:
            const findAction: boolean = isActionAlreadyExist(state, action.payload.type);
            if (findAction) {
                return {
                    ...state,
                    actions: state.actions.map((item, index) => {
                        if (action.payload.type === item.type) {
                            return {
                                ...item,
                                count: item.count + 1,
                            };
                        }
                        return item;
                    }),
                };
            } else {
                return {
                    ...state,
                    actions: [...state.actions, { type: action.payload.type, id: action.payload?.id, count: 1 }],
                };
            }
        case types.STOP_PENDING_OPERATION:
            const foundAction = state.actions.find((el) => el.type === action.payload.type);
            if (foundAction && foundAction.count > 1) {
                return {
                    ...state,
                    actions: state.actions.map((item, index) => {
                        if (action.payload.type === item.type) {
                            return {
                                ...item,
                                count: item.count - 1,
                            };
                        }
                        return item;
                    }),
                };
            } else {
                return {
                    ...state,
                    actions: state.actions.filter((el) => el.type !== action.payload.type),
                };
            }
        case types.CLEAR_ALL:
            return initialState;
        default:
            return state;
    }
};
export default reducer;

const localState = (state: any): PendingOperationStateState => state[name] || initialState;
export const selectors = {
    actions: (state) => localState(state).actions,
    isAppLoading: (state) => selectors.actions(state).length >= 1,
    /** True while one of the given action types is in flight, e.g. to show a form's submit loader. */
    isPending: (state, ...actionTypes: string[]): boolean =>
        findPendingOperation(selectors.actions(state), ...actionTypes),
    isLogging: (state) => findPendingOperation(selectors.actions(state), loginTypes.LOGIN),
    isGettigUserStorage: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), homeTypes.USERSTORAGE_GET),
    isLoadingGetPages: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), tableTypes.GET_PAGES),
    loadingRecoverPaidRow: (state) =>
        findPendingOperationWithId(selectors.actions(state), billTypes.SIMPLE_BILLS_RECOVER)[0],
    loadingUnrecoverablePaidRow: (state) =>
        findPendingOperationWithId(selectors.actions(state), billTypes.SIMPLE_BILLS_UNRECOVERABLE)[0],
    loadingRetryPaiement: (state) =>
        findPendingOperationWithId(selectors.actions(state), billTypes.SIMPLE_RETRY_PAIEMENT)[0],
    isLoadingOrders: (state: PendingOperationStateState): boolean =>
        findPendingOperation(
            selectors.actions(state),
            bookingTypes.CHECK_FLIGHT,
            bookingTypes.CREATE_PRICE_CONFIRMATION,
            bookingTypes.CONFIRM_PRICE_CONFIRMATION,
            reservationTypes.CREATE_RESERVATION,
        ),
    isLoadingEditReservations: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), reservationTypes.EDIT_RESERVATION),
    isLoadingAreas: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), pricingTypes.GET_AREAS, pricingTypes.GET_AREA),
    isComputingTariff: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), pricingTypes.COMPUTE_TARIFF),
    isLoadingAgent: (state: PendingOperationStateState): boolean =>
        findPendingOperation(
            selectors.actions(state),
            viaDialogTypes.GET_ALL_PHONES,
            viaDialogTypes.CONNECT_FREE_SEATING_USER,
            viaDialogTypes.DISCONNECT_FREE_SEATING_USER,
        ),
    isLoadingSnappay: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), snappayTypes.GET_STATS, snappayTypes.GET_TRANSACTIONS),
    isRecalculatingRefund: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), bookingTypes.RECALCULATE_REFOUND),
    isGettingCompanyFid: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), companyTypes.GET_FID_PROGRAM),
    isLoadingTravelPolicy: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), companyTypes.GET_TRAVEL_POLICIES),
    isLoadingComment: (state: PendingOperationStateState): boolean =>
        findPendingOperation(
            selectors.actions(state),
            genericTypes.GET_COMMENT,
            genericTypes.SAVE_COMMENT,
            genericTypes.DELETE_COMMENT,
        ),
    isCreatingTicket: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), ticketingTypes.CREATE_ISSUE, ticketingTypes.UPDATE_ISSUE),
    isSuspendingOrUnsuspending: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), driversTypes.SUSPEND_DRIVER, driversTypes.UNSUSPEND_DRIVER),
    isLoadingDriverPoints: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), driversTypes.GET_DRIVER_POINTS),
    isLoadingControlTowers: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), towerTypes.GET_TOWERS),
    isLoadingRanks: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), rankTypes.GET_RANKS),
    isLoadingRank: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), rankTypes.GET_RANK, rankTypes.GET_AREA),
    isLoadingDriverCommunications: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), driverCommunicationTypes.GET_DRIVER_COMMUNICATIONS),
    isLoadingDriverCommunication: (state: PendingOperationStateState): boolean =>
        findPendingOperation(selectors.actions(state), driverCommunicationTypes.GET_DRIVER_COMMUNICATION),
    isSavingDriverCommunication: (state: PendingOperationStateState): boolean =>
        findPendingOperation(
            selectors.actions(state),
            driverCommunicationTypes.CREATE_DRIVER_COMMUNICATION,
            driverCommunicationTypes.UPDATE_DRIVER_COMMUNICATION,
            driverCommunicationTypes.DELETE_DRIVER_COMMUNICATION,
        ),
    isCreatingPartnerContract: (state: PendingOperationStateState): boolean =>
        findPendingOperation(
            selectors.actions(state),
            partnerTypes.SAVE_PARTNERSHIP_CONTRACT,
            partnerTypes.REGENERATE_PARTNERSHIP_CONTRACT,
        ),
};
